import { siteUrl } from "./constants";
import { mediaUrl } from "./imageUtils";
interface ContainerImage {
  url?: string;
  formats?: {
    thumbnail?: { url: string };
    small?: { url: string };
    medium?: { url: string };
    large?: { url: string };
  };
}

interface Container {
  img?: { url: string };
  created_at?: string;
  url?: string | Array<{ url: string }>;
  image?: ContainerImage | null;
  bgImg?: {
    formats: {
      medium: {
        url: string;
      };
    };
  };
  formats?: {
    thumbnail?: { url: string };
    small?: { url: string };
    medium?: { url: string };
    large?: { url: string };
  };
}

/**
 * Converts an integer to its Roman numeral representation
 * @param num - The number to convert (1-3999)
 * @returns The Roman numeral string representation
 * @example
 * convertToRoman(9) // returns "IX"
 * convertToRoman(2024) // returns "MMXXIV"
 */
export const convertToRoman = (num: number): string => {
  const decimalValue = [1000, 900, 500, 400, 100, 90, 50, 40, 10, 9, 5, 4, 1];
  const romanNumeral = [
    "M",
    "CM",
    "D",
    "CD",
    "C",
    "XC",
    "L",
    "XL",
    "X",
    "IX",
    "V",
    "IV",
    "I",
  ];

  let romanized = "";
  let indexLimit = num;

  for (let index = 0; index < decimalValue.length; index += 1) {
    while (decimalValue[index] <= indexLimit) {
      romanized += romanNumeral[index];
      indexLimit -= decimalValue[index];
    }
  }

  return romanized;
};

/**
 * Determines the appropriate image URL based on container data and device type
 * @param container - The container object with various possible image properties
 * @param isMobileOnly - Whether to return mobile-optimized image size
 * @returns The complete image URL or empty string if no image found
 * @example
 * const imageUrl = imgSize(myContainer, true) // returns mobile-optimized image URL
 */
export const imgSize = (
  container?: Container | null,
  isMobileOnly?: boolean
): string => {
  if (container) {
    // 1. container.image
    if (!isMobileOnly && container.image?.url) {
      return mediaUrl(container.image.url);
    }

    // 2. container.img
    if (container.img) {
      return mediaUrl(container.img.url);
    }

    // 3. Mobile-specific formats
    if (isMobileOnly && container.image?.formats?.medium) {
      return mediaUrl(container.image.formats.medium.url);
    }

    // 5. Fallback URL handling
    if (container.url) {
      const url = Array.isArray(container.url)
        ? container.url[0]?.url
        : container.url;
      return url ? mediaUrl(url) : "";
    }
  } else {
    console.warn("No container provided to imgSize function");
  }

  return "";
};

/**
 * Smoothly scrolls the window to the top of the page
 * @returns void
 * @example
 * scrollToTop() // Scrolls to top of page
 */
export const scrollToTop = (): void => {
  document.body.scrollTop = 0; // For Safari
  document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
};
/**
 * Retrieves an item from session storage
 * @param key - The storage key to retrieve
 * @returns The stored string value or null if not found
 * @example
 * const token = getStorageItem('authToken')
 */
export const getStorageItem = (key: string): string | null =>
  sessionStorage.getItem(key);
/**
 * Parses a JSON item from session storage
 * @param key - The storage key to retrieve and parse
 * @returns The parsed JSON object or null if parsing fails
 * @throws Logs error to console if JSON parsing fails
 * @example
 * const userData = parseStorageItem('user') as User
 */
export const parseStorageItem = (key: string): unknown => {
  let storageItem;
  try {
    storageItem = getStorageItem(key);
    if (!storageItem) {
      return null;
    }
    return JSON.parse(storageItem);
  } catch (error) {
    console.error(`Error parsing storage item '${key}':`, error, storageItem);
    return null;
  }
};
/**
 * Stores an item in session storage as JSON
 * @param key - The storage key
 * @param item - The item to store (will be JSON stringified)
 * @returns void
 * @example
 * setStorageItem('user', { name: 'John', id: 123 })
 */
export const setStorageItem = (key: string, item: unknown): void =>
  sessionStorage.setItem(key, JSON.stringify(item));
